/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { ApolloClient } from '@apollo/client'
import { Service } from '../../api'
import { DISABLE_USER, ENABLE_USER } from './graphql/mutations'
import {
  GET_EMAIL_ADDRESS_METADATA_BY_EMAIL_ADDRESS,
  GET_ENTITLEMENTS_FOR_USER,
  LIST_EMAIL_ADDRESSES_BY_SUB,
  LIST_FUNDING_SOURCES_BY_SUB,
  LIST_VIRTUAL_CARDS_BY_SUB,
  RETRIEVE_USER_BY_SUB,
  RETRIEVE_USER_BY_USERNAME,
} from './graphql/queries'
import {
  DisableUser,
  EmailAddress,
  EmailMetaData,
  EnableUser,
  Entitlement,
  EntitlementsForUser,
  FundingSource,
  FundingSources,
  HydratedUserInfo,
  UserBySub,
  UserByUsername,
  UserEmailAddresses,
  UserInfo,
  VirtualCard,
} from './types'

export const getUserEntitlements = async (
  projectClient: ApolloClient<unknown>,
  externalId: string,
): Promise<Entitlement[]> => {
  const { data, errors } = await projectClient.query<EntitlementsForUser>({
    query: GET_ENTITLEMENTS_FOR_USER,
    variables: { externalId },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })

  const noEntitlementsError = errors?.find(
    (error) => error.errorType === 'sudoplatform.NoEntitlementsError',
  )
  if (noEntitlementsError) {
    return []
  }

  if (errors?.length) {
    console.error('Error retrieving entitlements.')
    errors.forEach(console.error)
    return []
  }

  return data.getEntitlementsForUser.entitlements.entitlements
}

export const getUserEmails = async (
  projectClient: ApolloClient<unknown>,
  sub: string,
): Promise<EmailAddress[]> => {
  const { data, errors } = await projectClient.query<UserEmailAddresses>({
    query: LIST_EMAIL_ADDRESSES_BY_SUB,
    variables: { sub },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })

  if (errors?.length) {
    console.error('Error retrieving email addresses.')
    errors.forEach(console.error)
    return []
  }

  return data.listEmailAddressesBySub
}

export const getFundingSources = async (
  projectClient: ApolloClient<unknown>,
  sub: string,
): Promise<FundingSource[]> => {
  const { data, errors } = await projectClient.query<FundingSources>({
    query: LIST_FUNDING_SOURCES_BY_SUB,
    variables: { sub },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })

  if (errors?.length) {
    console.error('Error retrieving funding sources.')
    errors.forEach(console.error)
    return []
  }

  return data.listFundingSourcesBySub
}

export const getVirtualCards = async (
  projectClient: ApolloClient<unknown>,
  sub: string,
): Promise<VirtualCard[]> => {
  const { data, errors } = await projectClient.query<{
    listVirtualCardsBySub: VirtualCard[]
  }>({
    query: LIST_VIRTUAL_CARDS_BY_SUB,
    variables: { sub },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })

  if (errors?.length) {
    console.error('Error retrieving virtual cards.')
    errors.forEach(console.error)
    return []
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = data.listVirtualCardsBySub.map((card: any) => ({
    id: card.id,
    state: card.state,
    last4: card.last4,
    owners: card.owners,
    keyId: card.keyId,
    keyRingId: card.keyRingId,
    createdAt: new Date(card.createdAtEpochMs),
    updatedAt: new Date(card.updatedAtEpochMs),
    cancelledAt: card.cancelledAtEpochMs
      ? new Date(card.cancelledAtEpochMs)
      : undefined,
  }))

  return result
}

const hasService = (serviceName: string, services: Service[]): boolean =>
  services.some((s) => s.id === serviceName)

export const hydrateUserInfo = async (
  projectClient: ApolloClient<unknown>,
  data: UserInfo,
  services: Service[],
): Promise<HydratedUserInfo> => {
  const userInfo = {
    enabled: data.enabled,
    lastModifiedDate: data.lastModifiedDate,
    registeredDate: data.registeredDate,
    username: data.username,
    sub: data.sub,
  }

  const entitlementsPromise = getUserEntitlements(projectClient, data.username)
  const userEmailsPromise = hasService('email-service', services)
    ? getUserEmails(projectClient, data.sub)
    : Promise.resolve(null)
  const virtualCardsPromise = hasService('virtual-cards-service', services)
    ? getVirtualCards(projectClient, data.sub)
    : Promise.resolve(null)
  const fundingSourcesPromise = hasService('virtual-cards-service', services)
    ? getFundingSources(projectClient, data.sub)
    : Promise.resolve(null)

  try {
    const [entitlements, userEmails, virtualCards, fundingSources] =
      await Promise.all([
        entitlementsPromise,
        userEmailsPromise,
        virtualCardsPromise,
        fundingSourcesPromise,
      ])
    return {
      ...userInfo,
      entitlements,
      userEmails,
      virtualCards,
      fundingSources,
    }
  } catch (e) {
    console.error('Error hydrating info; returning partial user data.', e)
    return userInfo
  }
}

export const getUserByUsername = async (
  projectClient: ApolloClient<unknown>,
  searchTerm: string,
): Promise<UserInfo> => {
  const { data, errors } = await projectClient.query<UserByUsername>({
    query: RETRIEVE_USER_BY_USERNAME,
    variables: { username: searchTerm },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })
  const userNotFoundError = errors?.find(
    (error) => error.errorType === 'sudoplatform.identity.UserNotFound',
  )
  if (userNotFoundError) {
    throw new Error('User not found.')
  }

  return data.retrieveUserByUsername
}

export const getFullUserByUsername = async (
  projectClient: ApolloClient<unknown>,
  searchTerm: string,
  services: Service[],
): Promise<HydratedUserInfo> => {
  const userInfo = await getUserByUsername(projectClient, searchTerm)

  return hydrateUserInfo(projectClient, userInfo, services)
}

export const getFullUserBySub = async (
  projectClient: ApolloClient<unknown>,
  searchTerm: string,
  services: Service[],
): Promise<HydratedUserInfo> => {
  const { data, errors } = await projectClient.query<UserBySub>({
    query: RETRIEVE_USER_BY_SUB,
    variables: { sub: searchTerm },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })
  const userNotFoundError = errors?.find(
    (error) => error.errorType === 'sudoplatform.identity.UserNotFound',
  )

  if (userNotFoundError) {
    throw new Error('User not found.')
  }

  return hydrateUserInfo(projectClient, data.retrieveUserBySub, services)
}

export const getFullUserByEmail = async (
  projectClient: ApolloClient<unknown>,
  searchTerm: string,
  services: Service[],
): Promise<HydratedUserInfo> => {
  const { data } = await projectClient.query<EmailMetaData>({
    query: GET_EMAIL_ADDRESS_METADATA_BY_EMAIL_ADDRESS,
    variables: { emailAddress: searchTerm },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })

  if (!data.getEmailAddressMetadataByEmailAddress) {
    throw new Error('User not found.')
  }

  return getFullUserBySub(
    projectClient,
    data.getEmailAddressMetadataByEmailAddress.owner,
    services,
  )
}

export const disableUser = async (
  projectClient: ApolloClient<unknown>,
  searchTerm: string,
  submit: Function,
): Promise<void> => {
  const { data } = await projectClient.mutate<DisableUser>({
    mutation: DISABLE_USER,
    variables: { username: searchTerm },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })

  if (data?.disableUser.success) {
    submit({ searchParam: 'username', searchTerm })
  }
}

export const enableUser = async (
  projectClient: ApolloClient<unknown>,
  searchTerm: string,
  submit: Function,
): Promise<void> => {
  const { data } = await projectClient.mutate<EnableUser>({
    mutation: ENABLE_USER,
    variables: { username: searchTerm },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })
  if (data?.enableUser.success) {
    submit({ searchParam: 'username', searchTerm })
  }
}
